import { PriceAndPromotionInfo } from "shared/src/types/PriceAndPromotionInfo";
import { FullPriceDisplayData, PriceDisplayData } from "../datatypes/Price";
import { PriceConfig } from "shared/src/types/articlesearch/Price";

type SecondaryPriceConfig = "DoNotShowUnitPriceForBundle" | string;

export function getDisplayPriceData(
    loadedPrice: PriceAndPromotionInfo,
    priceConfig: PriceConfig,
    isWeightArticle: boolean,
    secondaryPriceConfig: SecondaryPriceConfig | undefined | null
): FullPriceDisplayData {
    if (loadedPrice) {
        return processConfig(loadedPrice, priceConfig, isWeightArticle, secondaryPriceConfig);
    } else {
        return {
            valid: false
        };
    }
}

export function getPositionDisplayPrice(
    loadedPrice: PriceAndPromotionInfo | null | undefined,
    priceConfig: PriceConfig
): number | null | undefined {
    if (!loadedPrice) {
        return loadedPrice;
    }
    const isGross = primaryIsGross(priceConfig);
    const includeTaxes = includeMarginTaxesInPrice(priceConfig);
    if (!includeTaxes) {
        if (!isGross) {
            return loadedPrice.positionNet;
        } else {
            return loadedPrice.positionGross;
        }
    } else {
        if (!isGross) {
            return loadedPrice.positionNetWithTaxes;
        } else {
            return loadedPrice.positionGrossWithTaxes;
        }
    }
}

// possible combos: cip && dnr, promotion && dnr
function getPriceClass(price: PriceAndPromotionInfo) {
    // NOTE: the order of ifs is important
    if (price.isManualPrice) {
        return "manual";
    }
    if (price.isCipBbp) {
        return "cip";
    }

    const isCip = price.isCip;
    const isPromotion = price.isPromotion;
    const isDnr = price.isDnr;
    const isActiveDnr = price.isDnrActive;

    if (isCip && !isActiveDnr) {
        return "cip";
    }

    const classes = [];

    if (price.strikeThroughNet || isPromotion || isActiveDnr) {
        classes.push("promotion");
    }

    if (isDnr) {
        classes.push("volume-discount");
    }

    if (isActiveDnr) {
        classes.push("active");
    }

    return classes.join(" ");
}

export function primaryIsNet(config: PriceConfig): boolean {
    return !primaryIsGross(config);
}

export function primaryIsGross(config: PriceConfig): boolean {
    switch (config.priceBreakdown.primaryPriceType) {
        case "net":
            return false;
        case "gross":
            return true;
        default:
            return !!config.priceCompact.primary?.toLocaleLowerCase()?.match(/gross/);
    }
}

export function includeMarginTaxesInPrice(config: PriceConfig): boolean {
    return config.priceBreakdown.includeMarginTaxesInPrice;
}

function processConfig(
    price: PriceAndPromotionInfo,
    config: PriceConfig,
    isWeightArticle: boolean,
    secondaryPriceConfig: SecondaryPriceConfig | undefined | null
): FullPriceDisplayData {
    if (price && config && price.valid && config.priceCompact) {
        const includeMarginTaxes = includeMarginTaxesInPrice(config);
        const args = { isWeightArticle, secondaryPriceConfig, includeMarginTaxes };
        return {
            valid: price.valid,
            currency: price.currency,

            primary: getConfig(config.priceCompact.primary, price, args),
            secondary: getConfig(config.priceCompact.secondary, price, args)
        };
    } else {
        return {
            valid: false
        };
    }
}

type AdditionalArgs = {
    isWeightArticle: boolean;
    secondaryPriceConfig: SecondaryPriceConfig | undefined | null;
    includeMarginTaxes: boolean;
};

function getConfig(type: string, price: PriceAndPromotionInfo, args: AdditionalArgs): PriceDisplayData {
    if (typeof typedHandlers[type] === "function") {
        return typedHandlers[type](price, args);
    } else {
        return handlers.none();
    }
}

const handlers = {
    unitNet(price: PriceAndPromotionInfo, { includeMarginTaxes }: AdditionalArgs) {
        return {
            amount: includeMarginTaxes ? price.unitNetWithTaxes : price.unitNet,
            strikeThrough: price.strikeThroughNet,
            renderDescriptionOnly: false,
            style: getPriceClass(price),
            info: "PRICE_WITH_LABEL_NET",
            unitIndicatorWeight: "EX_ARTICLE_KG"
        };
    },

    unitGross(price: PriceAndPromotionInfo, { includeMarginTaxes }: AdditionalArgs) {
        return {
            amount: includeMarginTaxes ? price.unitGrossWithTaxes : price.unitGross,
            strikeThrough: price.strikeThroughGross,
            renderDescriptionOnly: false,
            style: getPriceClass(price),
            info: "PRICE_WITH_LABEL_GROSS",
            unitIndicatorWeight: "EX_ARTICLE_KG"
        };
    },

    unitGrossPriceOnly(price: PriceAndPromotionInfo, { includeMarginTaxes }: AdditionalArgs) {
        return {
            amount: includeMarginTaxes ? price.unitGrossWithTaxes : price.unitGross,
            strikeThrough: price.strikeThroughGross,
            renderDescriptionOnly: false,
            style: getPriceClass(price),
            info: ""
        };
    },

    pieceNet(price: PriceAndPromotionInfo) {
        return {
            amount: price.pieceNet,
            strikeThrough: null, // does not exist yet
            renderDescriptionOnly: false,
            style: getPriceClass(price),
            info: "PRICE_WITH_LABEL_PIECE_NET",
            unitIndicatorWeight: "EX_ARTICLE_PIECE"
        };
    },

    pieceGross(price: PriceAndPromotionInfo) {
        return {
            amount: price.pieceGross,
            strikeThrough: null, // does not exist yet
            renderDescriptionOnly: false,
            style: getPriceClass(price),
            info: "PRICE_WITH_LABEL_PIECE_GROSS",
            unitIndicatorWeight: "EX_ARTICLE_PIECE"
        };
    },

    perKgLNet(price: PriceAndPromotionInfo) {
        return {
            amount: price.kgNet,
            strikeThrough: null, // does not exist yet
            renderDescriptionOnly: false,
            style: getPriceClass(price),
            info: "PRICE_WITH_LABEL_PER_KG_L_NET",
            unitIndicatorWeight: "EX_ARTICLE_KG"
        };
    },

    // Either use the price per Kg or the piece price if that is not set
    perUnitNet(price: PriceAndPromotionInfo, { secondaryPriceConfig }: AdditionalArgs) {
        const perKgLNet = handlers.perKgLNet(price);
        const pieceNet = handlers.pieceNet(price);

        if (perKgLNet.amount !== undefined && perKgLNet.amount !== null) {
            return perKgLNet;
        }
        if (secondaryPriceConfig === "DoNotShowUnitPriceForBundle") {
            return {
                amount: null,
                strikeThrough: null,
                renderDescriptionOnly: false,
                style: getPriceClass(price),
                info: "PRICE_WITH_LABEL_PIECE_NET",
                unitIndicatorWeight: "EX_ARTICLE_PIECE"
            };
        } else {
            return pieceNet;
        }
    },

    perKgLGross(price: PriceAndPromotionInfo) {
        return {
            amount: price.kgGross,
            strikeThrough: null, // does not exist yet
            renderDescriptionOnly: false,
            style: getPriceClass(price),
            info: "PRICE_WITH_LABEL_PER_KG_L_GROSS",
            unitIndicatorWeight: "EX_ARTICLE_KG"
        };
    },

    depositNet(price: PriceAndPromotionInfo) {
        return {
            amount: price.depositNet,
            strikeThrough: null, // does not exist yet
            renderDescriptionOnly: false,
            style: "deposit",
            info: "PRICE_WITH_LABEL_DEPOSIT_NET"
        };
    },

    depositGross(price: PriceAndPromotionInfo) {
        return {
            amount: price.depositGross,
            strikeThrough: null, // does not exist yet
            renderDescriptionOnly: false,
            style: "deposit vat",
            info: "PRICE_WITH_LABEL_DEPOSIT_GROSS"
        };
    },

    serviceFeeNet(price: PriceAndPromotionInfo) {
        return {
            amount: null, // does not yet exist
            strikeThrough: null, // does not exist yet
            style: getPriceClass(price),
            renderDescriptionOnly: false,
            info: "PRICE_WITH_LABEL_SERVICE_FEE_NET"
        };
    },

    serviceFeeGross(price: PriceAndPromotionInfo) {
        return {
            amount: null, // does not yet exist
            strikeThrough: null, // does not exist yet
            style: getPriceClass(price),
            renderDescriptionOnly: false,
            info: "PRICE_WITH_LABEL_SERVICE_FEE_GROSS"
        };
    },

    vatIncludedHint(price: PriceAndPromotionInfo) {
        return {
            amount: null,
            strikeThrough: null, // does not exist yet
            style: getPriceClass(price),
            renderDescriptionOnly: true,
            info: "VAT_INCLUDED_PRICE_HINT"
        };
    },

    none() {
        return {
            amount: null,
            strikeThrough: null,
            style: "",
            info: null
        };
    }
};

// This makes sure that the handler functions all take the same parameters and can be generically called in getConfig.
// Still keeping the `handlers` object separately allows us to call functions from it directly and leave out the parameters that that particular function doesn't need.
const typedHandlers: {
    [x: string]: (x0: PriceAndPromotionInfo, args: AdditionalArgs) => PriceDisplayData;
} = handlers;
